import { useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  ContactFormFields,
  isPersonalEmail,
} from '@transcend-io/contact-form-schema';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { useConsentManager } from '#/lib/consent-manager';
import { checkFormServerError } from '#/lib/contact-form-client/helpers';
import { setPendingContactSubmission } from '#/lib/contact-form-client/pending-submission';
import { submitLead } from '#/lib/contact-form-client/submit';
import { assignAndKeepSubmitting } from '#/lib/rails-form/assignAndKeepSubmitting';
import {
  getRailsJumpUrl,
  RAILS_FORM_METADATA,
  RAILS_LANDING_TRACKING_EVENT,
} from '#/lib/rails-form/constants';
import type { SanityBasicText } from '#/lib/sanity/queries/basicText';
import type { SanityContactForm } from '#/lib/sanity/queries/form';
import { useAnalytics, useConversionEvent } from '#/lib/segment';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';

import LoadingSpinner from '../LoadingSpinner';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './FormControls';
import { generateAnalyticsId, useFormMetadata } from './utils';

const homepageEmailSchema = z.object({
  email: ContactFormFields.shape.email.refine(
    (value) => !isPersonalEmail(value),
    {
      message: 'Please enter a work email address.',
    },
  ),
});

type HomepageEmailFields = z.infer<typeof homepageEmailSchema>;

export type HomepageEmailFormFunnel = 'contact' | 'rails';

type Props = {
  /** Required for the contact funnel; unused when formFunnel is `rails`. */
  form?: SanityContactForm | null;
  formFunnel?: HomepageEmailFormFunnel;
  primaryButtonLabel?: string;
  emailPlaceholder?: string;
  theme?: 'light' | 'dark';
  className?: string;
  /**
   * Optional privacy copy under the email field. When set, wins over the
   * contact form’s `legalText` (needed for the Rails funnel, which has no form).
   */
  legalText?: SanityBasicText | null;
  legalTextClassName?: string;
};

/**
 * Homepage email capture. Validates a work email, upserts as an incomplete
 * submission, then continues to /contact or the Rails product jump URL.
 *
 * Rails funnel opts into `forwardOnAbandon` so Clay still receives the lead
 * after the grace period if they leave the Rails app before finishing.
 */
export function HomepageEmailForm({
  form,
  formFunnel = 'contact',
  primaryButtonLabel = 'Get a demo',
  emailPlaceholder = 'Work email',
  theme,
  className,
  legalText,
  legalTextClassName,
}: Props) {
  const router = useRouter();
  const { asPath } = router;
  const { airgap } = useConsentManager();
  const analytics = useAnalytics();
  const fireConversionEvent = useConversionEvent();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const isRailsFunnel = formFunnel === 'rails';

  if (!isRailsFunnel && !form) {
    throw new Error('HomepageEmailForm requires a form for the contact funnel');
  }

  const contactForm = form;
  const { methods, getMetadata } = useFormMetadata({
    hubspotCampaignId: isRailsFunnel
      ? RAILS_FORM_METADATA.hubspotCampaignId
      : contactForm!.hubspotCampaignId,
    hubspotConvertingFormType: isRailsFunnel
      ? RAILS_FORM_METADATA.hubspotConvertingFormType
      : contactForm!.hubspotConvertingFormType,
    hubspotEmailSubscriptionIds: isRailsFunnel
      ? RAILS_FORM_METADATA.hubspotEmailSubscriptionIds
      : contactForm!.hubspotEmailSubscriptionIds,
    trackingEventName: isRailsFunnel
      ? RAILS_LANDING_TRACKING_EVENT
      : contactForm!.trackingEventName,
    formFieldsSchema: homepageEmailSchema,
    formTitle: isRailsFunnel
      ? RAILS_FORM_METADATA.formTitle
      : contactForm!.formTitle,
    path: asPath,
  });

  useEffect(() => methods.setValue('consent', true), [methods]);

  const analyticsId = generateAnalyticsId(
    isRailsFunnel ? RAILS_FORM_METADATA.formTitle : contactForm!.formTitle,
    asPath,
  );

  useEffect(() => {
    if (!isRailsFunnel) {
      void router.prefetch('/contact');
    }
  }, [router, isRailsFunnel]);

  const submitRailsEmailAndContinue = async (fields: HomepageEmailFields) => {
    const trackingPurposes = Object.fromEntries(
      Object.keys(airgap?.getConsent().purposes ?? {}).map((purpose) => [
        purpose,
        airgap?.isConsented(new Set([purpose])) ?? false,
      ]),
    );

    const recaptchaToken =
      executeRecaptcha &&
      (await executeRecaptcha('contact_form_submission_started'));
    const metadata = await getMetadata(recaptchaToken);

    const response = await submitLead(
      { ...fields, consent: true },
      { ...metadata, trackingPurposes },
      {
        analytics,
        isSubmissionComplete: false,
        // Clay/HubSpot after 8m if they leave before finishing on Rails.
        forwardOnAbandon: true,
      },
    );

    if ('error' in response) {
      methods.setError('root', {
        type: 'server',
        message: response.error,
      });
      return;
    }

    fireConversionEvent(fields.email, 'Contact Form Submission Started', {
      formId: analyticsId,
      submissionId: response.submissionId,
      successfullyEnriched: false,
      ...contactForm?.trackingEventProperties,
    });

    await assignAndKeepSubmitting(getRailsJumpUrl(response.submissionId));
  };

  const handleSubmit: SubmitHandler<HomepageEmailFields> = async (fields) => {
    try {
      if (isRailsFunnel) {
        await submitRailsEmailAndContinue(fields);
        return;
      }

      const trackingPurposes = Object.fromEntries(
        Object.keys(airgap?.getConsent().purposes ?? {}).map((purpose) => [
          purpose,
          airgap?.isConsented(new Set([purpose])) ?? false,
        ]),
      );

      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha('contact_form_submission_started'));
      const metadata = await getMetadata(recaptchaToken);

      const response = await submitLead(
        { ...fields, consent: true },
        { ...metadata, trackingPurposes },
        { analytics, isSubmissionComplete: false },
      );

      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        return;
      }

      fireConversionEvent(fields.email, 'Contact Form Submission Started', {
        formId: analyticsId,
        submissionId: response.submissionId,
        successfullyEnriched: false,
        ...contactForm?.trackingEventProperties,
      });

      setPendingContactSubmission({
        submissionId: response.submissionId,
        email: fields.email,
      });

      await router.push('/contact');
    } catch (err) {
      console.error(err);
      methods.setError('root', { type: 'server' });
    }
  };

  const serverError = checkFormServerError(methods.formState);
  const isBusy = methods.formState.isSubmitting;
  const privacyText =
    legalText && legalText.length > 0
      ? legalText
      : contactForm?.legalText && contactForm.legalText.length > 0
        ? contactForm.legalText
        : null;

  return (
    <div className={cn('relative', className)}>
      <FormProvider {...methods}>
        <form
          name={analyticsId}
          onSubmit={methods.handleSubmit(handleSubmit)}
          className={cn(
            'm:form-input flex justify-between p-0 m:p-0 overflow-clip flex-col m:flex-row gap-y-10 m:gap-y-0',
            theme === 'dark' && 'm:form-input-dark',
          )}
        >
          <FormField
            name="email"
            control={methods.control}
            defaultValue=""
            render={({ field: fieldProps }) => (
              <FormItem className="h-full flex-1">
                <FormLabel className="sr-only">Work email</FormLabel>
                <FormControl>
                  <input
                    type="email"
                    autoComplete="work email"
                    required
                    placeholder={emailPlaceholder}
                    className={cn(
                      'max-m:form-input size-full px-20 m:focus:outline-none m:border-none bg-transparent h-50',
                      theme === 'dark'
                        ? 'max-m:form-input-dark autofill-override-dark'
                        : 'autofill-override',
                    )}
                    disabled={isBusy}
                    {...fieldProps}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            variant="form"
            formTheme={theme}
            type="submit"
            disabled={isBusy}
            className={cn(
              'grid place-items-center min-w-160 border-none md:rounded-r-none',
              {
                'bg-electric-06': isBusy && theme === 'dark',
              },
              {
                'bg-grayscale-06': isBusy && theme !== 'dark',
              },
            )}
          >
            <span
              className={cn('row-start-1 col-start-1', isBusy && 'opacity-0')}
            >
              {primaryButtonLabel}
            </span>
            {isBusy ? (
              <LoadingSpinner
                formTheme={theme}
                className="row-start-1 col-start-1 absolute size-22"
              />
            ) : null}
          </Button>
        </form>
        <FormField
          name="email"
          control={methods.control}
          render={() => (
            <FormMessage className="legal-text mt-4 text-flame-06" />
          )}
        />
        {serverError ? (
          <p className="legal-text mt-4 text-flame-06">{serverError.message}</p>
        ) : null}
        {privacyText ? (
          <BasicText
            className={cn(
              'legal-text mt-10 font-light text-grayscale-05',
              theme === 'dark' && 'text-cloud',
              legalTextClassName,
            )}
            value={privacyText}
          />
        ) : null}
      </FormProvider>
    </div>
  );
}
